import React, { useEffect, useState } from "react";
import { PointsHistoryAPI } from "../../services/userService.js";
import "bootstrap/dist/css/bootstrap.min.css";
import { 
  TrophyFill, 
  BookHalf, 
  Controller, 
  LightningFill, 
  CalendarEvent, 
  FilterCircle,
  Award,
  StarFill,
  GraphUpArrow
} from "react-bootstrap-icons";

// --- Configuration (Unified Design System) ---
const ACTIVITY_CONFIG = {
  SPORTS: { 
    color: "#0d6efd", 
    bg: "#e7f1ff", 
    gradient: "linear-gradient(135deg, #0d6efd 0%, #4da3ff 100%)",
    icon: <LightningFill /> 
  },
  ACADEMIC: { 
    color: "#198754", 
    bg: "#e6f8ef", 
    gradient: "linear-gradient(135deg, #198754 0%, #63e6be 100%)",
    icon: <BookHalf /> 
  },
  ENTERTAINMENT: { 
    color: "#fd7e14", 
    bg: "#fff4e6", 
    gradient: "linear-gradient(135deg, #fd7e14 0%, #ffc078 100%)",
    icon: <Controller /> 
  },
  COMPETITIONS: { 
    color: "#6f42c1", 
    bg: "#f3ebff", 
    gradient: "linear-gradient(135deg, #6f42c1 0%, #d0bfff 100%)",
    icon: <TrophyFill /> 
  },
  DEFAULT: { 
    color: "#6c757d", 
    bg: "#f8f9fa", 
    gradient: "linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)",
    icon: <Award /> 
  },
};

export default function PointsHistory() {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("ALL");

  useEffect(() => {
    async function fetchHistory() {
      try {
        const data = await PointsHistoryAPI();
        setHistory(data.history || []);
        setFilteredHistory(data.history || []);
      } catch (err) {
        setError(err.message || "Unable to load points history.");
      } finally {
        setLoading(false);
      }
    }
    fetchHistory();
  }, []);

  const handleFilter = (type) => {
    setFilterType(type);
    if (type === "ALL") setFilteredHistory(history);
    else setFilteredHistory(history.filter((h) => h.activityType === type));
  };

  // Calculations
  const totalPoints = history.reduce((sum, item) => sum + item.points, 0);
  const totalActivities = history.length;
  const pointsByType = history.reduce((acc, item) => {
    acc[item.activityType] = (acc[item.activityType] || 0) + item.points;
    return acc;
  }, {});

  // Determine Rank/Level Message (Optional Gamification logic)
  const getMotivationalMessage = () => {
    if (totalPoints === 0) return "Start your journey today!";
    if (totalPoints < 100) return "Great start! Keep going.";
    if (totalPoints < 500) return "You are doing amazing!";
    return "You are a Campus Legend!";
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="min-vh-100 pb-5" style={{ backgroundColor: "#f8f9fc", paddingTop: "80px" }}>
      <div className="container">
        
        {/* --- Hero Section (Personalized) --- */}
        <div className="text-center mb-5 fade-in-up">
           <div className="d-inline-flex align-items-center gap-2 bg-white px-4 py-2 rounded-pill shadow-sm mb-3 border">
              <StarFill className="text-warning" /> 
              <span className="fw-semibold text-dark small">{getMotivationalMessage()}</span>
           </div>
           <h2 className="fw-bolder display-5 text-dark">
             My <span className="text-primary position-relative">Achievements
                <svg className="position-absolute start-0 bottom-0 w-100" height="10" viewBox="0 0 100 10" preserveAspectRatio="none" style={{zIndex: -1, opacity: 0.2}}>
                  <path d="M0 5 Q 50 10 100 5" stroke="#0d6efd" strokeWidth="8" fill="none" />
                </svg>
             </span>
           </h2>
           <p className="text-muted">Track your progress and participation history.</p>
        </div>

        {/* --- Stats Overview --- */}
        <div className="row g-3 mb-5 fade-in-up" style={{ animationDelay: "0.1s" }}>
          {/* Main Score Card */}
          <div className="col-12 col-md-6 col-lg-4">
             <div className="card border-0 shadow-lg rounded-4 overflow-hidden h-100 text-white" 
                  style={{ background: "linear-gradient(135deg, #0d6efd 0%, #0043a8 100%)" }}>
                <div className="card-body p-4 position-relative">
                   <div className="position-absolute top-0 end-0 p-3 opacity-25">
                      <TrophyFill size={80} />
                   </div>
                   <h6 className="text-uppercase opacity-75 fw-bold small mb-2">Total Points Earned</h6>
                   <h1 className="display-3 fw-bold mb-0">{totalPoints}</h1>
                   <div className="mt-3 bg-white bg-opacity-25 rounded-pill px-3 py-1">
                      Current Score
                   </div>
                </div>
             </div>
          </div>

          {/* Activities Count */}
          <div className="col-6 col-md-3 col-lg-4">
             <div className="card border-0 shadow-sm rounded-4 h-100 bg-white">
                <div className="card-body p-4 d-flex flex-column justify-content-center align-items-center text-center">
                   <div className="p-3 rounded-circle bg-light mb-3 text-primary">
                      <CalendarEvent size={28} />
                   </div>
                   <h2 className="fw-bold text-dark mb-0">{totalActivities}</h2>
                   <span className="text-muted small">Activities Joined</span>
                </div>
             </div>
          </div>

          {/* Top Category */}
          <div className="col-6 col-md-3 col-lg-4">
             <div className="card border-0 shadow-sm rounded-4 h-100 bg-white">
                <div className="card-body p-4 d-flex flex-column justify-content-center align-items-center text-center">
                   <div className="p-3 rounded-circle bg-light mb-3 text-success">
                      <GraphUpArrow size={28} />
                   </div>
                   <h5 className="fw-bold text-dark mb-0">{getHighestCategory(pointsByType)}</h5>
                   <span className="text-muted small">Top Interest</span>
                </div>
             </div>
          </div>
        </div>

        {/* --- Filter Tabs --- */}
        <div className="d-flex justify-content-center flex-wrap gap-2 mb-4 fade-in-up" style={{ animationDelay: "0.2s" }}>
            <FilterButton active={filterType === "ALL"} onClick={() => handleFilter("ALL")} label="All History" icon={<FilterCircle />} />
            {Object.keys(ACTIVITY_CONFIG).filter(k => k !== 'DEFAULT').map(type => (
               <FilterButton 
                  key={type}
                  active={filterType === type} 
                  onClick={() => handleFilter(type)} 
                  label={type} 
                  color={ACTIVITY_CONFIG[type].color}
               />
            ))}
        </div>

        {/* --- Activity Cards List --- */}
        <div className="row g-4 fade-in-up" style={{ animationDelay: "0.3s" }}>
          {filteredHistory.length > 0 ? (
            filteredHistory.map((item) => {
               const config = ACTIVITY_CONFIG[item.activityType] || ACTIVITY_CONFIG.DEFAULT;
               return (
                 <div key={item.id} className="col-12 col-md-6 col-lg-4">
                   <div className="activity-card h-100 position-relative bg-white rounded-4 shadow-sm p-4 d-flex flex-column justify-content-between">
                      {/* Top Accent Line */}
                      <div className="position-absolute top-0 start-0 w-100 rounded-top-4" style={{ height: "6px", background: config.gradient }}></div>

                      <div>
                         <div className="d-flex justify-content-between align-items-start mb-3">
                            <span className=" rounded-pill fw-bold" style={{ backgroundColor: config.bg, color: config.color }}>
                               {item.activityType}
                            </span>
                            <span className="text-muted small" style={{fontSize: "0.8rem"}}>
                               {new Date(item.date).toLocaleDateString("en-GB")}
                            </span>
                         </div>
                         
                         <div className="d-flex align-items-center gap-3 mb-3">
                            <div className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center" 
                                 style={{ width: "50px", height: "50px", background: config.gradient, color: "white", boxShadow: "0 4px 10px rgba(0,0,0,0.1)" }}>
                               {config.icon}
                            </div>
                            <h6 className="fw-bold text-dark mb-0 lh-base">
                               {item.activityName}
                            </h6>
                         </div>
                      </div>

                      <div className="d-flex align-items-center justify-content-between mt-3 pt-3 border-top border-light border-opacity-50">
                         <span className="text-muted small">Points Earned</span>
                         <span className="fw-bolder fs-4" style={{ color: config.color }}>+{item.points}</span>
                      </div>
                   </div>
                 </div>
               );
            })
          ) : (
            <div className="col-12 text-center py-5">
               <div className="mb-3 text-muted opacity-25"><TrophyFill size={60} /></div>
               <h5 className="fw-bold text-muted">No activities found yet.</h5>
               <p className="text-muted small">Join an activity to start earning points!</p>
            </div>
          )}
        </div>

      </div>

      {/* --- Styles --- */}
      <style>{`
        .fade-in-up {
          animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
          transform: translateY(20px);
        }
        @keyframes fadeInUp {
          to { opacity: 1; transform: translateY(0); }
        }
        .activity-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: 1px solid rgba(0,0,0,0.03);
        }
        .activity-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.08) !important;
        }
        .filter-btn {
           transition: all 0.2s ease;
           border: 1px solid #eee;
           background: white;
           color: #6c757d;
        }
        .filter-btn:hover {
           background-color: #f8f9fa;
           transform: translateY(-2px);
        }
        .filter-btn.active {
           background-color: #fff;
           border-color: currentColor;
           box-shadow: 0 4px 10px rgba(0,0,0,0.05);
           font-weight: 600;
        }
      `}</style>
    </div>
  );
}

// --- Helpers ---

function FilterButton({ active, onClick, label, icon, color }) {
   return (
      <button 
        onClick={onClick}
        className={`filter-btn btn rounded-pill px-4 py-2 d-flex align-items-center gap-2 ${active ? 'active' : ''}`}
        style={{ color: active && color ? color : undefined }}
      >
        {icon}
        <span>{label}</span>
      </button>
   );
}

function LoadingState() {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
      <div className="spinner-border text-primary mb-3" role="status"></div>
      <p className="text-muted fw-semibold">Loading achievements...</p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100 bg-light">
      <div className="text-center">
        <div className="text-danger mb-3"><Award size={40} /></div>
        <h5 className="text-danger fw-bold">Oops!</h5>
        <p className="text-muted">{message}</p>
      </div>
    </div>
  );
}

function getHighestCategory(pointsMap) {
   let max = 0;
   let maxKey = "None";
   Object.entries(pointsMap).forEach(([key, val]) => {
      if(val > max) { max = val; maxKey = key; }
   });
   return maxKey.charAt(0) + maxKey.slice(1).toLowerCase();
}